import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { KoruAppConfig } from '@/types'
import { api } from '@/lib/api'
import { DEFAULT_ACCENT, DEFAULT_APP_NAME, DEFAULT_LOGO_URL, applyBrand } from '@/lib/whitelabel'

export interface Brand {
  appName: string
  logoUrl?: string
  primaryColor: string
  shortDomain?: string
}

interface BrandContextValue extends Brand {
  /** Re-lee /api/brand (tras guardar en el editor de white label, o tras loguear: en el
   *  host único, /api/brand solo resuelve con token — no hay Host que lo identifique antes). */
  refresh: () => void
  /** Pinta una marca resuelta por otra vía (login de dos pasos: /api/auth/resolve, antes
   *  de tener token). `undefined` vuelve a lo que devuelva /api/brand. */
  setPreview: (brand: KoruAppConfig | undefined) => void
}

const Ctx = createContext<BrandContextValue>({
  appName: DEFAULT_APP_NAME,
  primaryColor: DEFAULT_ACCENT,
  refresh: () => {},
  setPreview: () => {},
})

export function BrandProvider({ children }: { children: React.ReactNode }) {
  const [overrides, setOverrides] = useState<KoruAppConfig>({})
  const [preview, setPreview] = useState<KoruAppConfig | undefined>(undefined)
  const [nonce, setNonce] = useState(0)

  const refresh = useCallback(() => setNonce((n) => n + 1), [])

  // Overrides de marca guardados por el admin (nuestro KV, endpoint público/autenticado
  // según resuelva el tenant por Host o por token).
  useEffect(() => {
    let alive = true
    api
      .get<KoruAppConfig>('/brand')
      .then((b) => alive && setOverrides(b || {}))
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [nonce])

  // Prioridad: preview (login de dos pasos) > override guardado > default.
  const brand = useMemo<Brand>(() => {
    const source = preview ?? overrides
    return {
      appName: source.appName?.trim() || DEFAULT_APP_NAME,
      logoUrl: source.logoUrl?.trim() || DEFAULT_LOGO_URL,
      primaryColor: source.primaryColor?.trim() || DEFAULT_ACCENT,
      shortDomain: source.shortDomain?.trim() || undefined,
    }
  }, [overrides, preview])

  useEffect(() => {
    applyBrand(brand)
  }, [brand])

  const value = useMemo<BrandContextValue>(() => ({ ...brand, refresh, setPreview }), [brand, refresh])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useBrand(): BrandContextValue {
  return useContext(Ctx)
}
